<script setup>
import { ref, computed } from "vue";
import { useGlobalStore } from "@stores/global.js";
import { useShopifyStore } from "@stores/shopify.js";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import Product from "@components/cards/Product.vue";
import MagnifyingGlass from "@components/icons/MagnifyingGlass.vue";
import Loading from "@components/icons/Loading.vue";
import ArrowLeft from "@components/icons/ArrowLeft.vue";
import ArrowRight from "@components/icons/ArrowRight.vue";
import TransitionExpand from "@components/transitions/TransitionExpand.vue";

const slider = ref({});
const breakpoints = {
	0: {
		slidesPerView: 1.25,
		spaceBetween: 8,
	},
	768: {
		slidesPerView: 2.2,
		spaceBetween: 8,
	},
	1024: {
		slidesPerView: 4,
		spaceBetween: 8,
	},
};

const onSwiper = (swiper) => {
	slider.value = swiper;
};

const global = useGlobalStore();
const shopify = useShopifyStore();

const query = computed({
	get() {
		return shopify.search.query;
	},
	set(value) {
		shopify.setSearchQuery(value);
	},
});

const searchMaxHeight = computed(() => {
	if (global.announcementBarVisible) {
		return "calc(100dvh - var(--header-height) - var(--announcement-bar-height))";
	}
	return "calc(100dvh - var(--header-height))";
});

const isSearchVisible = computed(() => {
	return global.search || shopify.search.query.length > 0;
});

const closeSearch = () => {
	global.toggleSearch(false);
	shopify.search.query = "";
	shopify.search.results = [];
};
</script>

<template>
	<TransitionExpand>
		<div
			v-if="isSearchVisible"
			class="absolute top-(--header-height) left-0 z-50 flex w-full items-start"
			:style="{ maxHeight: searchMaxHeight, minHeight: searchMaxHeight }"
			v-click-outside="closeSearch"
		>
			<div
				class="bg-theme w-full origin-top overflow-y-auto transition-colors"
				:style="{ maxHeight: searchMaxHeight }"
				v-click-outside="closeSearch"
			>
				<div class="overflow-hidden">
					<div class="container">
						<!-- Mobile search input -->
						<div class="py-16 lg:hidden">
							<div class="relative isolate">
								<label for="mobile-search" class="sr-only">
									Search products
								</label>
								<MagnifyingGlass
									class="text-theme absolute top-1/2 left-16 size-16 -translate-y-1/2"
								/>
								<input
									id="mobile-search"
									class="body-sm h-48 w-full rounded-lg bg-(--theme-surface)/10 pr-16 pl-40 text-(--theme-surface)"
									type="text"
									placeholder="Search products"
									v-model="query"
								/>
							</div>
						</div>
						<div
							class="flex items-center justify-center py-24 lg:py-48"
							v-if="shopify.search.loading"
						>
							<Loading class="mx-auto size-20 animate-spin" />
						</div>
						<div
							class="space-y-16 py-24 lg:space-y-24 lg:py-48"
							v-if="
								!shopify.search.loading &&
								shopify.search.results.length > 0
							"
						>
							<p class="lg:h4 h6 font-primary-condensed">
								{{ shopify.search.results.length }}
								{{
									shopify.search.results.length === 1
										? "item"
										: "items"
								}}
								found
							</p>
							<div class="relative isolate">
								<Swiper
									class="overflow-visible!"
									@swiper="onSwiper"
									:breakpoints="breakpoints"
								>
									<SwiperSlide
										v-for="product in shopify.search.results"
										:key="product.slug"
										@mouseenter="
											if (global.pointerFine) {
												global.setTheme(
													product.parent_collection?.theme
														?.background,
													product.parent_collection?.theme?.accent,
													product.parent_collection?.theme?.surface,
												);
											}
										"
										@mouseleave="global.setTheme(null, null, null)"
									>
										<Product
											:product="product"
											@click="closeSearch"
										/>
									</SwiperSlide>
								</Swiper>
								<button
									class="group absolute top-1/2 -left-24 z-10 hidden size-48 -translate-y-1/2 items-center justify-center rounded-full bg-(--theme-surface) text-(--theme-background) transition-all md:flex"
									@click.stop="slider.slidePrev()"
									aria-label="Previous image"
									v-if="!slider.isBeginning"
								>
									<ArrowLeft class="size-14 group-hover:opacity-75" />
								</button>
								<button
									class="group absolute top-1/2 -right-24 z-10 hidden size-48 -translate-y-1/2 items-center justify-center rounded-full bg-(--theme-surface) text-(--theme-background) transition-all md:flex"
									@click.stop="slider.slideNext()"
									aria-label="Next image"
									v-if="!slider.isEnd"
								>
									<ArrowRight class="size-14 group-hover:opacity-75" />
								</button>
							</div>
						</div>
						<div
							class="py-24 lg:py-48"
							v-if="
								!shopify.search.loading &&
								shopify.search.results.length === 0 &&
								shopify.search.query.length > 0
							"
						>
							<p class="lg:h4 h6 font-primary-condensed">
								No results found
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</TransitionExpand>
</template>
